import React, { useCallback, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import dashboard6Styles from "../styles/dashboard6Styles";

// Shared Matilda UI building blocks (modals, cards, chips) used by the migrated admin pages.

export function Dashboard6Styles() {
  return <style>{dashboard6Styles}</style>;
}

export function getInitials(first = "", last = "") {
  if (first && last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }
  const clean = (first || last || "").replace(/^(mr|mrs|ms|miss|dr|prof|engr|arch|barr|alh|alhaji)\.?\s+/i, "").trim();
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0].length >= 2) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (clean.slice(0, 2) || "??").toUpperCase();
}

export function useBootstrapModal(ref) {
  const show = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
  }, [ref]);
  const hide = useCallback(() => {
    if (!ref.current || !window.bootstrap?.Modal) return;
    window.bootstrap.Modal.getInstance(ref.current)?.hide();
  }, [ref]);
  return { show, hide };
}

export function PagePreloader({ visible }) {
  return (
    <div
      className={`page-preloader-overlay ${visible ? "is-visible" : ""}`}
      aria-hidden={!visible}
    >
      <div className="logo-box">
        <svg className="house-icon" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <path d="M 34 60 L 34 80 L 18 80 L 18 50 L 34 34 L 50 50 L 50 85" fill="none" stroke="#FFFFFF" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 45.31 22.69 L 66 43.37 L 66 85" fill="none" stroke="#BBEE6B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
          <path d="M 56.63 11.37 L 82 36.75 L 82 85" fill="none" stroke="#FFD75B" strokeWidth="10" strokeLinecap="butt" strokeLinejoin="miter" />
        </svg>
      </div>
    </div>
  );
}

export const SUCCESS_DOTS = [
  { top: "18%", left: "14%", size: 6, delay: 0 },
  { top: "70%", left: "10%", size: 5, delay: 0.3 },
  { top: "82%", left: "34%", size: 4, delay: 0.6 },
  { top: "12%", left: "80%", size: 5, delay: 0.2 },
  { top: "74%", left: "88%", size: 6, delay: 0.5 },
  { top: "90%", left: "62%", size: 4, delay: 0.8 },
];

export function SuccessState({ title, message, duration = 2400, onDone }) {
  const doneRef = useRef(onDone);
  useEffect(() => {
    doneRef.current = onDone;
  });
  useEffect(() => {
    const t = setTimeout(() => doneRef.current?.(), duration);
    return () => clearTimeout(t);
  }, [duration]);

  return (
    <div className="d6-success" role="status" aria-live="polite">
      <div className="d6-success-burst">
        <span className="d6-success-ring d6-success-ring-lg" />
        <span className="d6-success-ring d6-success-ring-md" />
        {SUCCESS_DOTS.map((dot, i) => (
          <span
            key={i}
            className="d6-success-dot"
            style={{ top: dot.top, left: dot.left, width: dot.size, height: dot.size, animationDelay: `${dot.delay}s` }}
          />
        ))}
        <svg viewBox="0 0 120 120" className="d6-success-check" aria-hidden="true">
          <circle cx="60" cy="60" r="52" className="d6-success-halo" />
          <circle cx="60" cy="60" r="44" className="d6-success-fill" />
          <circle cx="60" cy="60" r="44" className="d6-success-stroke" />
          <path d="M40 61 L54 75 L81 46" className="d6-success-tick" />
        </svg>
      </div>
      <h3 className="d6-success-title">{title}</h3>
      {message && <p className="d6-success-msg">{message}</p>}
      <div className="d6-success-progress">
        <span style={{ animationDuration: `${duration}ms` }} />
      </div>
    </div>
  );
}

export function ModalShell({ id, modalRef, icon, title, onClose, closeDisabled, subheader, footer, overlay, width = 720, fill = false, children }) {
  return (
    <div className="modal fade" id={id} ref={modalRef} tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered d6m-dialog" style={{ maxWidth: width }}>
        <div className="modal-content d6m">
          {overlay}
          <div className="d6m-head">
            <span className="d6m-head-icon"><i className={`ki-outline ${icon}`}></i></span>
            <div className="min-w-0 flex-grow-1">
              <h3 className="d6m-title text-truncate">{title}</h3>
            </div>
            <button type="button" className="d6m-x" onClick={onClose} disabled={closeDisabled} aria-label="Close">
              <i className="ki-outline ki-cross"></i>
            </button>
          </div>
          {subheader}
          <div className={`d6m-body ${fill ? "d6m-body-fill" : ""}`}>{children}</div>
          {footer && <div className="d6m-foot">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

export function StatusChip({ color = "#a1a1aa", children }) {
  return (
    <span className="d6m-chip" style={{ "--dot": color }}>
      {children}
    </span>
  );
}

export function Avatar({ src, name, size = 48, onClick }) {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [src]);
  const initials = getInitials(name || "");
  return (
    <span
      className={`d6m-avatar ${onClick ? "cursor-pointer" : ""}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
      onClick={onClick}
      title={onClick ? "View photo" : undefined}
    >
      {src && !failed ? <img src={src} alt="" onError={() => setFailed(true)} /> : initials}
    </span>
  );
}

export const Shimmer = ({ w = "100%", h = 12, r = 6, className = "" }) => (
  <span className={`d6m-shimmer ${className}`} style={{ width: w, height: h, borderRadius: r }} />
);

export function Field({ label, required, hint, error, children, className = "" }) {
  return (
    <div className={`d6m-field ${className}`}>
      <label>
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {error ? <div className="d6m-error">{error}</div> : hint ? <div className="d6m-hint">{hint}</div> : null}
    </div>
  );
}

export function SegTabs({ options, value, onChange }) {
  return (
    <div className="d6-seg">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`fs-8 fw-bold ${value === o.value ? "active" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function CardHead({ title, subtitle, children, className = "mb-4" }) {
  return (
    <div className={`d-flex align-items-start justify-content-between flex-wrap gap-3 ${className}`}>
      <div className="min-w-0">
        <h3 className="text-gray-900 d6-title mb-1">{title}</h3>
        {subtitle && <span className="text-gray-400 fs-8">{subtitle}</span>}
      </div>
      {children && <div className="d-flex align-items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export const TITLE_OPTIONS = [
  "Mr.", "Mrs.", "Ms.", "Miss", "Dr.", "Prof.", "Engr.", "Arch.", "Barr.", "Pharm.", "Senator", "Honorable", "Chief",
  "General", "Colonel", "Major", "Captain", "Lieutenant", "Pastor", "Reverend", "Bishop", "Apostle", "Prophet",
  "Evangelist", "Alhaji", "Alhaja", "Hajia", "Mallam", "Sheikh", "Imam",
];


export function PhoneInput({ value, onChange, invalid }) {
  return (
    <div className={`d6m-input-group ${invalid ? "is-invalid" : ""}`}>
      <span>+234</span>
      <input
        type="tel"
        maxLength={10}
        placeholder="8012345678"
        value={value.replace(/^\+234|^0/, "")}
        onChange={(e) => {
          const digits = e.target.value.replace(/[^0-9]/g, "").replace(/^0+/, "");
          onChange(digits ? `+234${digits}` : "");
        }}
      />
    </div>
  );
}


export function PageHeader({ breadcrumb, title, children }) {
  return (
    <div className="d-flex flex-stack flex-lg-row-fluid" id="kt_app_header_wrapper">
      <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3 mb-0" data-kt-swapper="1" data-kt-swapper-mode="{default: 'prepend', lg: 'prepend'}" data-kt-swapper-parent="{default: '#kt_app_content_container', lg: '#kt_app_header_wrapper'}">
        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 mb-2">
          <li className="breadcrumb-item text-gray-600 fw-bold lh-1">
            <Link to="/dashboard" className="text-gray-700 text-hover-primary me-1"><i className="ki-outline ki-home text-gray-700 fs-6"></i></Link>
          </li>
          <li className="breadcrumb-item"><i className="ki-outline ki-right fs-7 text-gray-700 mx-n1"></i></li>
          <li className="breadcrumb-item text-gray-600 fw-bold lh-1">{breadcrumb || title}</li>
        </ul>
        <h1 className="text-gray-900 d6-page-title m-0">{title}</h1>
      </div>
      {children && <div className="d-flex align-items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

// State-controlled variant of ModalShell (no bootstrap JS needed): render it only while `open` is true.
export function InlineModal({ open, onClose, icon, title, subheader, footer, width = 720, children }) {
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, [open]);
  if (!open) return null;
  return (
    <>
      <div className="modal-backdrop fade show" />
      <div className="modal fade show d-block" role="dialog" aria-modal="true" onClick={onClose}>
        <div className="modal-dialog modal-dialog-centered d6m-dialog" style={{ maxWidth: width }} onClick={(e) => e.stopPropagation()}>
          <div className="modal-content d6m">
            <div className="d6m-head">
              <span className="d6m-head-icon"><i className={`ki-outline ${icon}`}></i></span>
              <div className="min-w-0 flex-grow-1">
                <h3 className="d6m-title text-truncate">{title}</h3>
              </div>
              <button type="button" className="d6m-x" onClick={onClose} aria-label="Close">
                <i className="ki-outline ki-cross"></i>
              </button>
            </div>
            {subheader}
            <div className="d6m-body">{children}</div>
            {footer && <div className="d6m-foot">{footer}</div>}
          </div>
        </div>
      </div>
    </>
  );
}

// Green brand KPI strip. items: [{ icon: "ki-people", label, value, sub }]
export function KpiStrip({ items, loading = false }) {
  return (
    <div className="d6-kpi-brand p-6 mb-5">
      <div className="row g-4 g-xl-0 align-items-center position-relative" style={{ zIndex: 2 }}>
        {items.map((item, i) => (
          <div key={item.label} className={`col-12 col-sm-6 ${items.length === 3 ? "col-xl-4" : "col-xl-3"}`}>
            <div className={`px-4 py-2 h-100 ${i < items.length - 1 ? "border-end-xl border-white border-opacity-20" : ""}`}>
              <div className="d-flex align-items-center">
                <div className="symbol symbol-45px me-4 flex-shrink-0">
                  <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                    <i className={`ki-outline ${item.icon} fs-2 text-white`}></i>
                  </span>
                </div>
                <div className="flex-grow-1 min-w-0">
                  <div className="text-white text-opacity-80 fs-8 fw-semibold">{item.label}</div>
                  <div className="d6-num text-white my-0.5">{loading ? "—" : item.value}</div>
                  {item.sub && <div className="text-white text-opacity-70 fs-9">{item.sub}</div>}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Numbered pager. page is 1-based.
export function Pagination({ page, totalPages, onChange, summary }) {
  if (totalPages <= 1) return null;
  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-6 pt-5 border-top border-gray-200">
      <div className="text-gray-500 fs-8">{summary}</div>
      <div className="d-flex align-items-center gap-2">
        <button type="button" className="d6-icon-btn" disabled={page === 1} onClick={() => onChange(page - 1)}>
          <i className="ki-outline ki-left fs-5"></i>
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
          .reduce((acc, p, idx, arr) => {
            if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
            acc.push(p);
            return acc;
          }, [])
          .map((p, idx) =>
            p === "..." ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">…</span>
            ) : (
              <button
                key={p}
                type="button"
                className={`btn btn-sm d6-mbtn ${page === p ? "btn-success" : "btn-light"}`}
                style={{ height: 32, padding: "0 12px" }}
                onClick={() => onChange(p)}
              >
                {p}
              </button>
            )
          )}
        <button type="button" className="d6-icon-btn" disabled={page === totalPages} onClick={() => onChange(page + 1)}>
          <i className="ki-outline ki-right fs-5"></i>
        </button>
      </div>
    </div>
  );
}

// Checkbox list used by the "Download ..." export modals.
export function ColumnPicker({ options, setOptions, disabled = false, note }) {
  const keys = Object.keys(options);
  const allSelected = keys.every((k) => options[k]);
  return (
    <>
      {note && (
        <div className="d6m-note mt-0 mb-5">
          <i className="ki-outline ki-information-5"></i>
          <span>{note}</span>
        </div>
      )}
      <div className="d6m-section-head">
        <span>Columns to include</span>
        <button type="button" className="d6m-link-btn" onClick={() => setOptions(Object.fromEntries(keys.map((k) => [k, !allSelected])))}>
          {allSelected ? "Clear all" : "Select all"}
        </button>
      </div>
      <div className="d6m-list">
        {keys.map((key) => (
          <label key={key} className={`d6m-list-row d6m-check-row ${options[key] ? "active" : ""}`}>
            <input type="checkbox" checked={options[key]} onChange={(e) => setOptions({ ...options, [key]: e.target.checked })} disabled={disabled} />
            <span className="d6m-check" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M5 12.5 L10 17.5 L19 7.5" /></svg>
            </span>
            <span className="min-w-0 flex-grow-1">
              <span className="d6m-v d-block text-capitalize">{key.replace(/([A-Z])/g, " $1").trim()}</span>
            </span>
          </label>
        ))}
      </div>
    </>
  );
}
